import { EQUIPMENT_SUMMARY_KEY } from "../../../../utils/constants";
import { getEquipmentAnomalyClassOptions } from "../../../../utils/data";
import { ChecklistItem, EquipmentAnomalySummaryItem, FVIPDOChecklistItem, NewAnomalyItem } from "../../../../utils/models";
import { matchPhrases } from "../../../../utils/utilities";

import AccordionItem from "../accordion-item";
import Conditional from "../../../common/conditional";
import CustomAnomalyItems from "../../custom-general/custom-anomaly-items";
import ChecklistForm from "../checklist/checklistItem";
import EquipmentAnomalySummary from "../../../forms/custom-fvi/equipmentAnomalySummary";
import FVIPDOChecklistForm from "../visual-inspection-pdo/fvi-pdo-checklist-form";
import useMappedAnomalies from "../../../../hooks/useMappedAnomalies";

interface Props {
    equipmentId: number;
    isChecklistVisible: boolean;
    isPDOFormat: boolean;
    isNewFormat: boolean;
    equipmentNumber: string;
    hasAnomalies: 'yes' | 'no';
    additionalComment: string;
    nextAnomalyID: number;
    anomalySummary: EquipmentAnomalySummaryItem;
    anomalyItems: NewAnomalyItem[];
    checklistItems: ChecklistItem[];
    pdoChecklistItems: FVIPDOChecklistItem[];
    onChangePDOChecklistItems: (items: FVIPDOChecklistItem[]) => void;
    onChangeSummary: (summary: EquipmentAnomalySummaryItem) => void;
    onChangeAnomalies: (anomalies: NewAnomalyItem[]) => void;
    onChangeAdditionalComment: (comment: string) => void;
    onChangeChecklistItems: (items: ChecklistItem[]) => void;
}

const EquipmentOverview: React.FC<Props> = ({ additionalComment, anomalySummary, anomalyItems, checklistItems, equipmentId, equipmentNumber, isPDOFormat, pdoChecklistItems, onChangePDOChecklistItems, nextAnomalyID, hasAnomalies, isChecklistVisible, isNewFormat, onChangeAnomalies, onChangeSummary, onChangeAdditionalComment, onChangeChecklistItems }) => {
    const mappedAnomalyItems = useMappedAnomalies([{ id: equipmentId, anomalyItems }]);

    return (
        <>
            <Conditional isVisible={isChecklistVisible}>
                <AccordionItem isActive title="Checklist Items">
                    <ChecklistForm
                        enableChecklistItemComment={false}
                        label='Vessel Number'
                        anomalyItems={mappedAnomalyItems}
                        items={checklistItems}
                        lineNumber={equipmentNumber}
                        additionalComment={additionalComment}
                        onAddComment={onChangeAdditionalComment}
                        onItemChange={onChangeChecklistItems}
                    />
                </AccordionItem>
            </Conditional>

            {pdoChecklistItems && (
                <AccordionItem isActive title="Potential Drop Object Checklist">
                    <FVIPDOChecklistForm
                        hasPDO={isPDOFormat}
                        label="Equipment Tag"
                        value={equipmentNumber}
                        items={pdoChecklistItems}
                        onChangeItems={(items) => onChangePDOChecklistItems(items)}
                    />
                </AccordionItem>
            )}

            <Conditional isVisible={matchPhrases(hasAnomalies, 'yes')}>
                <AccordionItem isActive title="Anomaly Items">
                    <CustomAnomalyItems
                        isPDOFormat={isPDOFormat}
                        nextAnomalyID={nextAnomalyID}
                        locationOptions={EQUIPMENT_LOCATION_OPTIONS}
                        isNewFormat={isNewFormat}
                        damageClasses={getEquipmentAnomalyClassOptions()}
                        items={anomalyItems}
                        onDataChange={onChangeAnomalies}
                    />
                </AccordionItem>
            </Conditional>

            <AccordionItem isActive	title="Anomaly Summary & Description">
                <EquipmentAnomalySummary
                    useNewFormat={isNewFormat}
                    hasAnomalies={hasAnomalies}
                    initialAnomalySummary={anomalySummary} 
                    onAdd={onChangeSummary}
                />
            </AccordionItem>
        </>
    );
};

export const EQUIPMENT_LOCATION_OPTIONS = [
    {
        label: EQUIPMENT_SUMMARY_KEY.BODY,
        value: EQUIPMENT_SUMMARY_KEY.BODY
    },
    {
        label: EQUIPMENT_SUMMARY_KEY.FLANGE,
        value: EQUIPMENT_SUMMARY_KEY.FLANGE,
    },
    {
        label: EQUIPMENT_SUMMARY_KEY.FASTENERS,
        value: EQUIPMENT_SUMMARY_KEY.FASTENERS,
    },
    {
        label: EQUIPMENT_SUMMARY_KEY.VALVE,
        value: EQUIPMENT_SUMMARY_KEY.VALVE,
    },
    {
        label: EQUIPMENT_SUMMARY_KEY.UNDER_SUPPORT,
        value: EQUIPMENT_SUMMARY_KEY.UNDER_SUPPORT,
    },
    {
        label: EQUIPMENT_SUMMARY_KEY.SUPPORT,
        value: EQUIPMENT_SUMMARY_KEY.SUPPORT,
    },
    {
        label: EQUIPMENT_SUMMARY_KEY.PIPE,
        value: EQUIPMENT_SUMMARY_KEY.PIPE,
    },
    {
        label: EQUIPMENT_SUMMARY_KEY.GUAGE,
        value: EQUIPMENT_SUMMARY_KEY.GUAGE,
    },
    {
        label: EQUIPMENT_SUMMARY_KEY.DISH,
        value: EQUIPMENT_SUMMARY_KEY.DISH,
    },
    {
        label: EQUIPMENT_SUMMARY_KEY.TOP_DISH,
        value: EQUIPMENT_SUMMARY_KEY.TOP_DISH,
    },
    {
        label: EQUIPMENT_SUMMARY_KEY.BOTTOM_DISH,
        value: EQUIPMENT_SUMMARY_KEY.BOTTOM_DISH,
    },
    {
        label: EQUIPMENT_SUMMARY_KEY.NOZZLE,
        value: EQUIPMENT_SUMMARY_KEY.NOZZLE,
    },
    {
        label: EQUIPMENT_SUMMARY_KEY.NAME_PLATE,
        value: EQUIPMENT_SUMMARY_KEY.NAME_PLATE,
    },
    {
        label: EQUIPMENT_SUMMARY_KEY.SKIRT,
        value: EQUIPMENT_SUMMARY_KEY.SKIRT,
    },
    {
        label: EQUIPMENT_SUMMARY_KEY.STRUCTURE,
        value: EQUIPMENT_SUMMARY_KEY.STRUCTURE,
    },
    {
        label: EQUIPMENT_SUMMARY_KEY.EXTERNAL_PROTECTION,
        value: EQUIPMENT_SUMMARY_KEY.EXTERNAL_PROTECTION,
    },
    {
        label: EQUIPMENT_SUMMARY_KEY.INSULATION,
        value: EQUIPMENT_SUMMARY_KEY.INSULATION,
    },
    {
        label: EQUIPMENT_SUMMARY_KEY.LIFTING_LUG,
        value: EQUIPMENT_SUMMARY_KEY.LIFTING_LUG,
    },
    {
        label: EQUIPMENT_SUMMARY_KEY.STIFFENER_RING,
        value: EQUIPMENT_SUMMARY_KEY.STIFFENER_RING,
    },
    {
        label: EQUIPMENT_SUMMARY_KEY.SADDLE_SUPPORT,
        value: EQUIPMENT_SUMMARY_KEY.SADDLE_SUPPORT,
    },
];

export default EquipmentOverview;